
import React from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { ContentTab } from "@/components/admin/settings/ContentTab";
import { useAboutUs } from "@/hooks/useAboutUs";
import { useVendorHelp } from "@/hooks/useVendorHelp";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const AdminAboutUs = () => {
  const { aboutUs, isSaving, isLoading, saveAboutUsContent } = useAboutUs();
  const { vendorHelp, isSaving: isVendorSaving, isLoading: isVendorLoading, saveVendorHelpContent } = useVendorHelp();
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const handleSaveAboutUs = async (content: string) => {
    try {
      setErrorMessage(null);
      console.log("Saving about us content:", content);
      
      const success = await saveAboutUsContent(content);
      
      if (success) {
        console.log("About Us content saved successfully");
      }
    } catch (error: any) {
      const errorMsg = error.message || "Failed to save About Us content";
      setErrorMessage(errorMsg);
      console.error("Error saving About Us content:", error);
    }
  };

  const handleSaveVendorHelp = async (content: string) => {
    try {
      setErrorMessage(null);
      console.log("Saving vendor help content:", content);
      
      const success = await saveVendorHelpContent(content);
      
      if (success) {
        console.log("Vendor Help content saved successfully");
      }
    } catch (error: any) {
      const errorMsg = error.message || "Failed to save Vendor Help content";
      setErrorMessage(errorMsg);
      console.error("Error saving Vendor Help content:", error);
    }
  };

  if (isLoading || isVendorLoading) {
    return (
      <AdminLayout title="Content Management">
        <div className="space-y-4 w-full">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-[450px] w-full" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Content Management">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Content Management</h1>
      </div>
      
      {errorMessage && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}
      
      <Tabs defaultValue="about-us" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="about-us">About Us</TabsTrigger>
          <TabsTrigger value="vendor-help">Vendor Help</TabsTrigger>
        </TabsList>
        
        <TabsContent value="about-us" className="mt-6">
          <ContentTab 
            title="Edit About Us Page"
            description="Update the content that will be displayed on the About Us page."
            content={aboutUs.content}
            lastUpdated={aboutUs.lastUpdated}
            onSave={handleSaveAboutUs}
            isSaving={isSaving}
          />
        </TabsContent>
        
        <TabsContent value="vendor-help" className="mt-6">
          <ContentTab 
            title="Edit Vendor Help Page"
            description="Update the help content that vendors will see when they click the Help link in their navigation."
            content={vendorHelp.content}
            lastUpdated={vendorHelp.lastUpdated}
            onSave={handleSaveVendorHelp}
            isSaving={isVendorSaving}
          />
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default AdminAboutUs;
